import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectFullscreenToggle } from '../../utils/fullscreen-toggle';
import { Subscription, fromEvent } from 'rxjs';
import { CourseTimelineComponent } from '../course-timeline/course-timeline.component';
import { SplitScreenPlayerComponent } from '../split-screen-player/split-screen-player.component';
import { CoursePlayerHeaderComponent } from '../course-player-header/course-player-header.component';
import { IconComponent } from '../icon/icon.component';
import { InstructorPreviewMetadataStripComponent } from '../instructor-preview-metadata-strip/instructor-preview-metadata-strip.component';
import {
  CoursePlayerService,
  CourseNavigationItem,
  CourseStructure,
  CourseEntitlementBlockCode,
  PlayerMode,
  PlayerConfig
} from '../../../services/course-player.service';
import { PaymentsCheckoutService } from '../../../payments/payments-checkout.service';
import { formatCoursePriceLabel } from '../../../payments/course-pricing-format';
import { CourseService } from '../../../courses/course.service';
import { VideoProgressService } from '../../../services/video-progress.service';
import { ProfileService } from '../../../profile/profile.service';
import { AuthService } from '../../../auth/auth.service';
import { LearnerProgressService } from './services/learner-progress.service';
import { LearnerQuizService } from './services/learner-quiz.service';
import { LearnerNavigationService } from './services/learner-navigation.service';
import { LandingPageComponent } from './components/landing-page/landing-page.component';
import { CourseSummaryComponent } from './components/course-summary/course-summary.component';
import { UnitIntroComponent } from './components/unit-intro/unit-intro.component';
import { getModuleProgress, countItemsOfType, getUnitStatsForItem } from './learner-modal.utils';
import { decideCourseOrgAccess } from './pure-functions/course-org-access';
import { togglePreviewUnlockState } from './pure-functions/preview-unlock';
import { shouldShowOverrideChip as shouldShowOverrideChipFn } from './pure-functions/override-chip';
import { canLearnerSelfReset as canLearnerSelfResetFn } from './pure-functions/learner-reset';
import { computeUnitOrderGateReasons } from './pure-functions/unit-order-gate';
import { decideCompletionFeedback } from './pure-functions/completion-feedback';
import { readTimelineCollapsed, safeSessionStorage, writeTimelineCollapsed } from './pure-functions/timeline-preference';
import { DebugService } from '../../services/debug.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { Unit } from '../../../instructor/units.service';
import { PassingPolicyResultsService, UnitResultView, CourseResultView } from '../../passing-policy/passing-policy-results.service';
import {
  computeOverallCourseVerdict,
  isCourseVerdictBlocking,
  OverallCourseVerdict,
} from '@shared/passing-policy/passing-policy';
import { courseVerdictHeadline, courseAlertClass, courseBlockReason } from '../../passing-policy/course-pass-display.util';
import { learnerModalAnimations } from './course-player-learner-modal.animations';
import { CertificationStatusPanelComponent } from '../../certification/certification-status-panel.component';

export interface CoursePlayerLearnerModalData {
  courseId: string;
  startItemId?: string;
  playerMode?: PlayerMode;
  /**
   * When previewing a unit directly from the Content Hub (no owning course),
   * pass the hydrated Unit here. The player will build a course-like shell
   * from this unit instead of calling the backend. Only honored in
   * `instructor_preview` mode with an empty `courseId`.
   */
  previewUnit?: Unit;
}

@Component({
  selector: 'app-course-player-learner-modal',
  standalone: true,
  imports: [
    CourseTimelineComponent,
    SplitScreenPlayerComponent,
    CoursePlayerHeaderComponent,
    IconComponent,
    LandingPageComponent,
    CourseSummaryComponent,
    UnitIntroComponent,
    InstructorPreviewMetadataStripComponent,
    CertificationStatusPanelComponent,
    SlicePipe
],
  providers: [
    LearnerProgressService,
    LearnerQuizService,
    LearnerNavigationService
  ],
  templateUrl: './course-player-learner-modal.component.html',
  styleUrls: ['./course-player-learner-modal.component.scss'],
  animations: learnerModalAnimations
})
export class CoursePlayerLearnerModalComponent implements OnInit, OnDestroy {
  @Input() data!: CoursePlayerLearnerModalData;
  /**
   * Wave 2: Scope the player to a single unit. When true, no course_summary
   * is synthesized, `onModuleComplete` does not auto-jump to the summary,
   * and the template suppresses the summary subtree. Used with `previewUnit`
   * by Wave 3's `UnitViewModalComponent` embed.
   */
  @Input() lockToUnit = false;
  @Output() onClose = new EventEmitter<{ completed?: boolean; lastItemId?: string; orgMismatch?: boolean; accessDenied?: boolean; certificateId?: string }>();

  @ViewChild('modalContent') modalContent!: ElementRef;
  @ViewChild(CourseTimelineComponent) timelineComponent!: CourseTimelineComponent;

  // Modal state
  isLoading = true;
  isContentLoading = false;
  loadingError: string | null = null;
  // KAN-353: task-5.3 entitlement 403 on the structure load. When set, the
  // error state renders a Buy/Renew CTA instead of Retry (a retry cannot fix
  // an entitlement 403; buying can). Fulfillment stays webhook-only - the
  // CTA just starts a server-priced hosted checkout.
  entitlementBlockCode: CourseEntitlementBlockCode | null = null;
  checkoutBusy = false;
  checkoutError: string | null = null;
  // KAN-415: the base course read denied access for the learner player (403
  // COURSE_ACCESS_DENIED / 404 COURSE_NOT_FOUND) — a departed/removed learner
  // deep-linking an old course URL. Renders a clean "no longer have access"
  // state with a Back to My Courses action, never the retryable crash.
  accessDenied = false;
  accessDeniedMessage: string | null = null;
  showTimeline = true;
  isMobile = false;
  // KAN-561: the sidebar collapse choice lives for the browser session. Only
  // desktop (>= 1024px) interactions write it; the mobile auto-close and the
  // mobile overlay never touch it, so widening the window restores the choice.
  private readonly timelineStorage = safeSessionStorage();
  private screenSizeChecked = false;
  showCloseConfirmation = false;
  isFullscreen = false;
  // KAN-620: fullscreen on the modal only where the browser grants it; the
  // header button is hidden where it cannot (iPhone has no element fullscreen).
  private readonly fullscreen = injectFullscreenToggle({
    target: () => this.modalContent?.nativeElement,
    onChange: isFullscreen => {
      this.isFullscreen = isFullscreen;
      this.cdr.markForCheck();
    }
  });
  get fullscreenAvailable(): boolean {
    return this.fullscreen.isAvailable;
  }
  private orgMismatchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Course data
  courseStructure: CourseStructure | null = null;
  currentItem: CourseNavigationItem | null = null;
  currentItemIndex = 0;
  courseData: any = null;
  courseStarted = false;
  isReturningLearner = false;
  showLandingPage = true;

  // Player configuration
  playerMode: PlayerMode = 'learner';
  playerConfig!: PlayerConfig;

  // Preview-only: instructor can unlock navigation for this session without changing the course
  previewUnlocked = false;
  private lockedPlayerConfig: PlayerConfig | null = null;

  // Navigation state
  canGoToPrevious = false;
  canGoToNext = false;
  // KAN-597: the current item's completion is being saved (watch requirement
  // met on screen, or a quiz submit + progress reload in flight) and the server
  // has not answered. The footer shows "Saving progress..." instead of a stale
  // "Complete Module"; driven only by the request lifecycle, never a timer.
  completionPending = false;

  // Progress tracking
  courseProgressMap: Map<string, any> = new Map();
  currentUserId: string | null = null;

  // KAN-314 VS2-VS5: live layered-passing-policy results, indexed by unitId.
  unitResultsMap: Map<string, UnitResultView> = new Map();
  courseResult: CourseResultView | null = null;
  // KAN-318 VS4: unitId -> progression-gate lock reason (an earlier gated unit unpassed).
  private gateLockedReasons: Map<string, string> = new Map();

  // Quiz submission error flag - signals quiz-panel to reset spinner
  quizSubmissionError = false;
  /** KAN-349: parsed submit-error info (code/details) for the quiz panel. */
  quizSubmissionErrorInfo: { code?: string; message?: string; details?: any } | null = null;

  // Temporary progress for instructor preview mode
  private tempInstructorProgress: Map<string, any> = new Map();

  private destroyRef = inject(DestroyRef);
  private subscriptions: Subscription[] = [];

  constructor(
    private coursePlayerService: CoursePlayerService,
    private courseService: CourseService,
    private videoProgressService: VideoProgressService,
    private profileService: ProfileService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private progressService: LearnerProgressService,
    private quizSubmitService: LearnerQuizService,
    private navigationService: LearnerNavigationService,
    private passingResultsService: PassingPolicyResultsService,
    private checkoutService: PaymentsCheckoutService,
    public debug: DebugService,
    private errorHandler: ErrorHandlerService
  ) {}

  ngOnInit() {
    if (this.data.playerMode) {
      this.playerMode = this.data.playerMode;
    }

    const currentUser = this.profileService.getCurrentUser();
    this.currentUserId = currentUser?.id || null;

    this.playerConfig = this.coursePlayerService.getPlayerConfig(this.playerMode);

    this.checkScreenSize();
    fromEvent(window, 'resize').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.checkScreenSize());

    // KAN-198: react to backend-driven module completion (e.g. watch requirement
    // met during video playback). Without this, the sidebar checkmark and the
    // overall course-progress percent stay stale until a hard page refresh.
    this.videoProgressService.moduleCompleted.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((moduleId: string) => this.handleBackendModuleCompletion(moduleId));
    this.videoProgressService.completionSaveState.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ moduleId, pending }) => {
      if (this.currentItem?.id !== moduleId) return;
      this.setCompletionPending(pending);
    });

    this.loadCourse();

    document.addEventListener('keydown', this.handleKeyDown);
  }

  private handleBackendModuleCompletion(moduleId: string): void {
    const changed = this.progressService.applyBackendModuleCompletion(
      moduleId,
      this.courseStructure,
      this.courseProgressMap,
      this.cdr,
      this.timelineComponent,
      () => this.updateNavigationState(),
      this.lockToUnit,
      this.playerConfig.enforceSequential
    );
    if (changed) {
      // KAN-597: the module just completed server-side, so the next item may be
      // unlocked - re-resolve Next in place instead of leaving the footer on
      // "Complete Module" until the learner navigates or reloads.
      this.refreshNavigationGates();
      this.cdr.detectChanges();
      // Unit pass/fail results feed the VS4 gate; converge them too.
      this.loadPassingResults();
    }

    // KAN-238: instructor preview/test progress is ephemeral and lives in
    // `tempInstructorProgress`. `loadCourseProgress` rebuilds `courseProgressMap`
    // from it on every reload, so we must mirror the completion there too — otherwise
    // a 0%-watch-threshold video that auto-completes on Next would update the sidebar
    // checkmark only until the next reload, while the learner path persists via the
    // backend. Learner mode keeps using the backend-driven map as before.
    if (changed && (this.playerMode === 'instructor_preview' || this.playerMode === 'instructor_test')) {
      const item = this.courseStructure?.navigationItems.find(i => i.id === moduleId);
      const progressKey = (item ? this.coursePlayerService.getProgressKey(item) : null) || moduleId;
      const updated = this.courseProgressMap.get(progressKey);
      if (updated) {
        this.tempInstructorProgress.set(progressKey, updated);
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    document.removeEventListener('keydown', this.handleKeyDown);
    // Resize listener cleanup is handled automatically via takeUntilDestroyed
    if (this.orgMismatchTimeout) {
      clearTimeout(this.orgMismatchTimeout);
    }

    if (this.playerMode === 'instructor_preview' || this.playerMode === 'instructor_test') {
      this.tempInstructorProgress.clear();
    }
  }

  loadCourse(): void {
    this.isLoading = true;
    this.loadingError = null;
    this.entitlementBlockCode = null;
    this.checkoutError = null;
    this.accessDenied = false;
    this.accessDeniedMessage = null;

    this.progressService.loadCourse(
      this.data.courseId,
      this.playerMode,
      this.playerConfig,
      this.subscriptions,
      (result) => {
        // KAN-415: a departed/removed learner deep-linked an old course URL and
        // the base read denied access. Show a clean denial, not the crash.
        if (result.accessDenied) {
          this.handleCourseAccessDenied();
          return;
        }

        // Defense-in-depth: validate course belongs to user's current org
        if (result.courseData && !this.validateCourseOrgAccess(result.courseData)) {
          return;
        }

        this.courseData = result.courseData;
        this.courseStarted = result.courseStarted;
        this.isReturningLearner = result.isReturningLearner;
        this.showLandingPage = result.showLandingPage;
        this.playerConfig = result.playerConfig;
        if (result.playerMode) {
          this.playerMode = result.playerMode;
        }
        this.loadingError = result.loadingError;
        if (result.loadingError || result.isLoading === false) {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      },
      () => this.loadCourseStructure(),
      this.data.previewUnit
    );
  }

  private loadCourseStructure(): void {
    this.progressService.loadCourseStructure(
      this.data.courseId,
      this.subscriptions,
      (structure) => {
        this.courseStructure = structure;
        this.loadCourseProgress();
      },
      (error, entitlementCode) => {
        this.loadingError = error;
        this.entitlementBlockCode = entitlementCode ?? null;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      this.data.previewUnit
    );
  }

  // ===== Entitlement blocked state (KAN-353) =====

  get entitlementHeading(): string {
    return this.entitlementBlockCode === 'COURSE_ACCESS_EXPIRED' ? 'Access Expired' : 'Purchase Required';
  }

  get entitlementCtaLabel(): string {
    return this.entitlementBlockCode === 'COURSE_ACCESS_EXPIRED' ? 'Renew access' : 'Buy course';
  }

  /**
   * KAN-331 follow-up: the blocked-state gate previously sent the member to
   * Stripe checkout with no idea what they'd pay. `courseData` is already
   * populated by GET /courses/:id BEFORE the structure-load 403 fires (KAN-353
   * intentionally lets an unentitled member read a paid+published course's
   * metadata as its member-facing sales surface) and carries the camelized
   * pricing columns (amountCents/currency, migration 158) - the same fields
   * the public purchase page formats. Reuses formatCoursePriceLabel (DRY) so
   * the two surfaces never drift. Null when pricing data isn't available
   * (e.g. a stale cached response), in which case the gate just omits the line.
   */
  get entitlementPriceLabel(): string | null {
    const amountCents = this.courseData?.amountCents;
    const currency = this.courseData?.currency;
    if (typeof amountCents !== 'number' || !currency) {
      return null;
    }
    return formatCoursePriceLabel(amountCents, currency);
  }

  /** Starts a server-priced checkout for the blocked course (price never client-supplied). */
  onRenewAccess(): void {
    if (this.checkoutBusy) return;
    this.checkoutBusy = true;
    this.checkoutError = null;

    this.checkoutService.createCourseCheckoutSession(this.data.courseId).subscribe({
      next: session => this.checkoutService.redirectToHostedCheckout(session.url),
      error: err => {
        this.checkoutBusy = false;
        this.checkoutError = this.checkoutService.checkoutErrorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  loadCourseProgress(setupInitial: boolean = true, afterLoad?: () => void): void {
    const finish = () => {
      if (setupInitial) this.setupInitialState();
      this.isLoading = false;
      this.cdr.detectChanges();
      // KAN-314 VS2-VS5: refresh the live unit/course pass-fail results so the
      // unit intro readout reflects the latest submission.
      this.loadPassingResults();
      // KAN-326: compute unit-order locks now too - loadPassingResults skips
      // non-learner modes (e.g. instructor preview), but the completion gate
      // should still render there.
      this.computeGateLocks();
      afterLoad?.();
    };
    this.progressService.loadCourseProgress(
      this.data.courseId,
      this.playerMode,
      this.courseStructure,
      this.courseProgressMap,
      this.tempInstructorProgress,
      this.subscriptions,
      setupInitial,
      (progressMap) => {
        this.courseProgressMap = progressMap;
        // Run twice: once for regular progress, once after unit-quiz population.
        this.updateNavigationStatusesFromProgress();
        this.updateNavigationStatusesFromProgress();
        finish();
      },
      finish
    );
  }

  /**
   * KAN-314 VS2-VS5: fetch the learner's live unit/course pass-fail results and
   * index them by unitId for the unit-intro readout. Only meaningful when a
   * Passing Policy is configured (else the server returns an empty list).
   */
  private loadPassingResults(): void {
    if (this.playerMode !== 'learner' || !this.data.courseId) return;
    this.passingResultsService.getResults(this.data.courseId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res) => {
      this.unitResultsMap.clear();
      (res.units || []).forEach((u) => this.unitResultsMap.set(u.unitId, u));
      this.courseResult = res.course || null;
      this.computeGateLocks();
      // KAN-323: gate locks affect Prev/Next reachability - refresh the button
      // state now that the VS4 lock map is known.
      this.updateNavigationState();
      this.cdr.detectChanges();
    });
  }

  /**
   * Compute per-unit lock reasons from BOTH unit gates and index them by unitId:
   *   - KAN-318 VS4 (score gate): a unit is locked when an EARLIER unit has
   *     requirePassToAdvance set and the learner has not passed it.
   *   - KAN-326 (completion gate): when the course setting
   *     requireUnitsInOrder is on, a unit is locked until the immediately
   *     preceding unit is fully complete.
   * Both walk units in nav order and propagate forward. When both would lock a
   * unit, the VS4 (pass) reason wins since it is the stricter requirement.
   */
  private computeGateLocks(): void {
    this.gateLockedReasons.clear();
    const items = this.courseStructure?.navigationItems || [];

    // KAN-326 completion-order gate (display; server also enforces). Applies in
    // learner mode and in instructor preview (so an instructor can verify it),
    // but the preview-unlock toggle bypasses it like every other enforcement.
    const orderGateActive =
      !!(this.courseData && this.courseData.requireUnitsInOrder) && !this.previewUnlocked;
    const orderReasons = computeUnitOrderGateReasons(items, orderGateActive);
    orderReasons.forEach((reason, uid) => this.gateLockedReasons.set(uid, reason));

    // KAN-318 VS4 progression (pass) gate - overrides the completion reason.
    const orderedUnitIds: string[] = [];
    for (const it of items as any[]) {
      const uid = it.parentUnitId || it.unitId;
      if (uid && !orderedUnitIds.includes(uid)) orderedUnitIds.push(uid);
    }
    let blockReason: string | null = null;
    for (const uid of orderedUnitIds) {
      if (blockReason) this.gateLockedReasons.set(uid, blockReason);
      const r = this.unitResultsMap.get(uid);
      if (r && r.requirePassToAdvance && r.status !== 'passed' && !blockReason) {
        const need = r.thresholdApplied != null
          ? `Unit aggregate ${r.aggregatePercent}% - ${r.thresholdApplied}% required`
          : 'the previous unit must be passed';
        blockReason = `Locked until you pass the previous unit (${need}).`;
      }
    }
  }

  /** VS4: the gate-lock reason for the current item's unit, or null. */
  getCurrentUnitGateLock(): string | null {
    const item: any = this.currentItem;
    const uid = item ? (item.parentUnitId || item.unitId) : null;
    return uid ? this.gateLockedReasons.get(uid) || null : null;
  }

  /** The current unit-intro item's live pass-fail result, if any. */
  getCurrentUnitResult(): UnitResultView | null {
    const item: any = this.currentItem;
    if (!item) return null;
    const unitId = item.parentUnitId || item.unitId;
    return unitId ? this.unitResultsMap.get(unitId) || null : null;
  }

  private updateNavigationStatusesFromProgress(): void {
    this.progressService.syncProgressAndSummary(
      this.courseStructure,
      this.courseProgressMap,
      this.cdr,
      this.timelineComponent,
      () => this.updateNavigationState(),
      this.lockToUnit,
      this.playerConfig.enforceSequential
    );
    this.refreshNavigationGates();
  }

  /**
   * Re-derive everything that depends on item statuses. KAN-326: unit-order
   * locks, so a later unit unlocks live once the prior unit is finished.
   * KAN-597: then Prev/Next, which were only re-resolved on navigation, on a
   * course_summary insert, or after the unit-results fetch - so a completed
   * mid-course module kept its "Complete Module" footer until the learner
   * clicked it, navigated away, or reloaded.
   */
  private refreshNavigationGates(): void {
    this.computeGateLocks();
    this.updateNavigationState();
  }

  get isCourseComplete(): boolean {
    // KAN-411 round 3: "all modules done" is NOT genuine completion. The exit
    // popup must only show the "Course Complete!" congratulation when the pass
    // gate is also satisfied - otherwise a below-threshold / floor-violating
    // learner (whom the backend correctly did NOT complete or certify) saw a
    // congratulatory dialog. Layer the passing gate on top of module completion,
    // mirroring the backend completion-quiz-passing-gate.service.
    return (
      this.progressService.areAllModulesComplete(this.courseStructure, this.courseProgressMap) &&
      this.isPassingGateSatisfied()
    );
  }

  /**
   * KAN-411 round 4: the OVERALL course verdict - the per-unit results folded
   * with the course aggregate. `courseResult` alone is a SCOPED verdict (it
   * answers only "does the aggregate clear coursePassingThreshold?"), so
   * rendering it directly put a green "Course aggregate: 92.9% - Passed" above
   * a completion the per-quiz points floor had blocked.
   *
   * KAN-503: the banner is now verdict-only learner copy (no percentage, no
   * "aggregate") - its old percentage clashed with the KAN-465 running score,
   * whose denominator counts only attempted quizzes.
   */
  get overallVerdict(): OverallCourseVerdict {
    return computeOverallCourseVerdict([...this.unitResultsMap.values()], this.courseResult);
  }

  get courseVerdictLine(): string {
    return courseVerdictHeadline(this.overallVerdict);
  }

  get courseVerdictAlertClass(): string {
    return courseAlertClass(this.overallVerdict.status);
  }

  /** Why a course whose aggregate clears its threshold is still blocked (null otherwise). */
  get courseVerdictBlockReason(): string | null {
    return courseBlockReason(this.overallVerdict);
  }

  /**
   * KAN-411 round 3: client-side mirror of completion-quiz-passing-gate.service
   * .isSatisfied, using the live verdicts already loaded here (unitResultsMap +
   * courseResult).
   *
   * Round 3b: mirror the backend SHORT-CIRCUIT exactly. The gate only enforces
   * quiz passing when passing_policy.requireQuizPassingForCompletion === true;
   * anything else (explicit opt-out `false`, or absent/unconfigured) means
   * completion is legitimately submission/module-based (KAN-363 opt-out
   * semantics) and the gate is satisfied. Without this, an EXPLICIT OPT-OUT
   * course (which the backend completes AND certifies) would show the neutral
   * popup / "In Progress" summary here - contradicting the card, the cert, and
   * the opt-out contract.
   *
   * Round 4: the fold itself is now the shared `computeOverallCourseVerdict` the
   * banner renders, so the gate and the banner cannot disagree. The opt-out
   * short-circuit stays HERE and never moves into the verdict: a verdict is true
   * whether or not completion is gated on it, and an opt-out course with a
   * violated floor must still show "Not passed" while completing normally.
   */
  private isPassingGateSatisfied(): boolean {
    if (this.courseData?.passingPolicy?.requireQuizPassingForCompletion !== true) {
      return true;
    }
    return !isCourseVerdictBlocking(this.overallVerdict);
  }

  private setupInitialState(): void {
    if (!this.courseStructure) return;
    if (this.showLandingPage && this.playerMode === 'learner') return;
    this.doNavigateToContent();
  }

  private doNavigateToContent(): void {
    if (!this.courseStructure) return;
    const startItem = this.navigationService.navigateToContent(
      this.courseStructure, this.data.startItemId,
      this.isReturningLearner, this.courseProgressMap
    );
    if (startItem) {
      this.showLandingPage = false;
      this.navigateToItem(startItem);
    }
  }

  navigateToItem(item: CourseNavigationItem): void {
    if (!this.courseStructure) return;
    this.completionPending = false;
    const result = this.navigationService.navigateToItem(item, this.courseStructure, this.cdr);
    this.currentItem = result.currentItem;
    this.currentItemIndex = result.currentItemIndex;
    this.updateNavigationState();

    // KAN-191 Bug 1: refetch live quiz on soft-nav so instructor edits
    // propagate without a hard refresh. (See LearnerQuizService.)
    if (this.currentItem) {
      this.quizSubmitService.refreshLiveQuizData(
        this.currentItem, () => this.currentItem, this.cdr, this.subscriptions);
    }
  }

  /**
   * KAN-323: reports whether an item's unit is progression-gate locked (VS4,
   * KAN-318). Passed into the navigation service so Prev/Next/back-arrow reuse
   * the same gate as the content-level lock in the template.
   */
  private isItemGateLocked = (item: CourseNavigationItem): boolean => {
    const uid = (item as any)?.parentUnitId || (item as any)?.unitId;
    return uid ? this.gateLockedReasons.has(uid) : false;
  };

  private updateNavigationState(): void {
    const state = this.navigationService.updateNavigationState(
      this.courseStructure,
      this.currentItemIndex,
      this.playerConfig,
      this.courseProgressMap,
      this.isItemGateLocked
    );
    this.canGoToPrevious = state.canGoToPrevious;
    this.canGoToNext = state.canGoToNext;
  }

  /**
   * KAN-595: whether the learner is on the FINAL navigation item, derived from
   * the index rather than inferred from `canGoToNext`.
   *
   * The player footer used to label its completion button off `!canGoToNext`.
   * That boolean is false for four distinct reasons - no next item exists, the
   * next item is sequentially LOCKED, the gate map has not loaded yet, or
   * courseStructure is still null - and only the first is "last". On a
   * sequential course it made the button read "Complete Course" for as long as
   * module 2 stayed locked, telling a learner on module 1 of 3 the course was
   * over; it self-corrected only when the next module unlocked.
   *
   * Returns false while courseStructure is null: "we do not know yet" must not
   * render as "this is the end".
   *
   * `navigationItems` may have a `course_summary` item APPENDED once every
   * module is complete (LearnerProgressService.addCourseSummaryIfNeeded), so the
   * final module stops being the last item at that point - correctly, since the
   * summary is then a real forward destination.
   */
  get isLastNavigableItem(): boolean {
    const items = this.courseStructure?.navigationItems;
    if (!items?.length) return false;
    return this.currentItemIndex >= items.length - 1;
  }

  startCourse(): void {
    const startSub = this.courseService.startCourse(this.data.courseId).subscribe({
      next: () => {
        this.courseStarted = true;
        this.showLandingPage = false;
        this.doNavigateToContent();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[LearnerModal] Failed to start course:', error);
        alert('Failed to start course. Please try again.');
      }
    });
    this.subscriptions.push(startSub);
  }

  resumeCourse(): void { this.dismissLandingAndEnterContent(); }
  skipToContent(): void { this.dismissLandingAndEnterContent(); }

  private dismissLandingAndEnterContent(): void {
    this.showLandingPage = false;
    this.doNavigateToContent();
    this.cdr.detectChanges();
  }

  canLearnerSelfReset(): boolean {
    return canLearnerSelfResetFn({
      courseData: this.courseData,
      isReturningLearner: this.isReturningLearner
    });
  }

  async resetCourseProgress(): Promise<void> {
    // MUST await discardPendingSaves() to ensure any in-flight video progress saves
    // (e.g. from stopTracking() in ngOnDestroy, which Angular doesn't await)
    // complete BEFORE we send the reset API call. Otherwise the save can arrive
    // at the backend AFTER the reset DELETEs module_progress rows, and the UPSERT
    // will INSERT a new row, recreating the stale progress.
    await this.videoProgressService.discardPendingSaves();

    const resetSub = this.courseService.resetOwnProgress(this.data.courseId).subscribe({
      next: () => {
        // Reload the course from scratch
        this.isLoading = true;
        this.courseStarted = false;
        this.isReturningLearner = false;
        this.showLandingPage = true;
        this.courseProgressMap = new Map();
        this.courseStructure = null;
        this.currentItem = null;
        this.loadCourse();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[LearnerModal] Failed to reset progress:', error);
        alert(error?.error?.message || 'Failed to reset progress. Please try again.');
      }
    });
    this.subscriptions.push(resetSub);
  }

  toggleTimeline(): void {
    this.setTimelineVisible(!this.showTimeline);
    this.cdr.detectChanges();
  }

  // KAN-561: single write path for the sidebar. Desktop choices persist for
  // the session; mobile opens/closes are overlay state only and never
  // overwrite the stored desktop preference.
  private setTimelineVisible(visible: boolean): void {
    this.showTimeline = visible;
    if (!this.isMobile) {
      writeTimelineCollapsed(this.timelineStorage, !visible);
    }
  }

  toggleFullscreen(): void {
    this.fullscreen.toggle();
  }

  onTimelineItemSelect(item: CourseNavigationItem): void {
    if (!this.courseStructure) return;
    if (this.playerConfig.enforceSequential &&
        !this.navigationService.canAccessItem(item, this.playerConfig, this.courseStructure, this.courseProgressMap)) {
      return;
    }
    // KAN-561: on mobile the sidebar is a full-width overlay covering the
    // content, so picking an item must dismiss it to reveal the destination.
    if (this.isMobile) this.setTimelineVisible(false);
    this.navigateToItem(item);
  }

  togglePreviewUnlock(): void {
    const next = togglePreviewUnlockState({
      playerMode: this.playerMode,
      playerConfig: this.playerConfig,
      previewUnlocked: this.previewUnlocked,
      lockedPlayerConfig: this.lockedPlayerConfig
    });
    if (!next) return;
    this.playerConfig = next.playerConfig;
    this.previewUnlocked = next.previewUnlocked;
    this.lockedPlayerConfig = next.lockedPlayerConfig;
    this.cdr.markForCheck();
  }

  navigateToPrevious(): void { this.stepBy(-1, this.canGoToPrevious); }
  navigateToNext(): void { this.stepBy(1, this.canGoToNext); }

  private stepBy(delta: number, allowed: boolean): void {
    if (!allowed || !this.courseStructure) return;
    const target = this.courseStructure.navigationItems[this.currentItemIndex + delta];
    if (!target) return;
    // KAN-323: defense-in-depth - re-check the gate before landing so a locked
    // neighbor is unreachable even if a caller bypasses the disabled state.
    if (!this.navigationService.canNavigateTo(
      target, this.playerConfig, this.courseStructure, this.courseProgressMap, this.isItemGateLocked
    )) {
      return;
    }
    this.navigateToItem(target);
  }

  closeModal(): void { this.showCloseConfirmation = true; }
  cancelClose(): void { this.showCloseConfirmation = false; }
  confirmClose(): void {
    this.showCloseConfirmation = false;
    this.onClose.emit({ lastItemId: this.currentItem?.id });
  }
  /** KAN-679: close straight to the learner's certificate. The host owns the
   *  navigation, so it makes ONE (to /certificates/:id) instead of racing its
   *  usual close redirect. No exit confirmation: the learner asked to leave. */
  openCertificate(certificateId: string): void {
    this.showCloseConfirmation = false;
    this.onClose.emit({ lastItemId: this.currentItem?.id, certificateId });
  }
  onTimelineClose(): void { this.setTimelineVisible(false); }

  // KAN-561: below 1024px the sidebar is an overlay that starts closed (the
  // header toggle reopens it); at/above 1024px it is a column whose open state
  // is the stored session preference (default open). Re-run on every resize,
  // so crossing 1024 in either direction lands on the right state.
  private checkScreenSize(): void {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 1024;
    // Only re-derive on init or when crossing 1024, so a plain desktop resize
    // never fights a collapse made while storage was unavailable.
    if (!this.screenSizeChecked || wasMobile !== this.isMobile) {
      this.screenSizeChecked = true;
      this.showTimeline = this.isMobile
        ? false
        : !readTimelineCollapsed(this.timelineStorage);
    }
    this.cdr.markForCheck();
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    this.navigationService.handleKeyDown(
      event,
      this.canGoToPrevious,
      this.canGoToNext,
      () => this.navigateToPrevious(),
      () => this.navigateToNext(),
      () => this.toggleTimeline(),
      () => this.closeModal()
    );
  };

  /**
   * Defence-in-depth: validate course belongs to user's org. Pure decision
   * lives in `pure-functions/course-org-access.ts`; this wrapper just wires
   * the result into modal-level side effects (error banner + delayed close).
   */
  private validateCourseOrgAccess(courseData: any): boolean {
    const decision = decideCourseOrgAccess({
      courseData,
      currentOrgId: this.authService.getCurrentOrganizationId(),
      currentUser: this.authService.getCurrentUser()
    });
    if (decision.allowed) return true;

    console.warn(`[LearnerModal] ${decision.reason} Closing modal.`);
    this.loadingError = 'This course belongs to a different organization. Redirecting...';
    this.isLoading = false;
    this.courseData = null;
    this.courseStructure = null;
    this.currentItem = null;
    this.orgMismatchTimeout = setTimeout(() => this.onClose.emit({ orgMismatch: true }), 1500);
    return false;
  }

  /**
   * KAN-415: the base course read denied the learner (revoked org access or the
   * course was removed). Render a clean, non-retryable access-denied state — a
   * retry can never restore revoked access, so the generic "Error Loading
   * Course" + Retry crash is wrong here. Mirrors the KAN-353 entitlement clean
   * state; the learner returns to My Courses via `returnToMyCourses`.
   */
  private handleCourseAccessDenied(): void {
    this.accessDenied = true;
    this.accessDeniedMessage =
      'You no longer have access to this course. It may have been removed, or you may have left the organization that owns it.';
    this.loadingError = null;
    this.entitlementBlockCode = null;
    this.isLoading = false;
    this.courseData = null;
    this.courseStructure = null;
    this.currentItem = null;
    this.cdr.markForCheck();
  }

  /**
   * KAN-415: leave the denied course and return to My Courses. The consumer
   * (route wrapper on a deep-link, or the course list for an in-list modal)
   * handles the actual navigation/close via the `accessDenied` flag.
   */
  returnToMyCourses(): void {
    this.onClose.emit({ accessDenied: true });
  }

  getCurrentQuizQuestions(): any[] {
    return this.currentItem?.quiz || [];
  }

  getCurrentVideoInfo(): { videoId?: string; videoUrl?: string; cdnHostname?: string } {
    if (!this.currentItem || !this.currentItem.hasVideo) return {};
    // Per-org Bunny: forward the video's own CDN host so the split-screen player
    // builds the HLS playlist on the org's host instead of the shared host.
    return {
      videoId: this.currentItem.bunnyVideoId,
      videoUrl: this.currentItem.videoPath,
      cdnHostname: this.currentItem.cdnHostname
    };
  }

  onModuleComplete(): void {
    // After progress reloads we try to jump to the summary page. The finder
    // returns a summary item only once every module is complete; otherwise the
    // jump is skipped — by design in the middle of the course.
    //
    // KAN-367: if the reload does NOT surface a summary, never leave a silent
    // no-op while something is still unmet - tell the learner what is blocking.
    //
    // When `lockToUnit` is true (single-unit preview), we never emit a
    // course_summary item AND we skip the jump outright, so no message either.
    const completingItem = this.currentItem;
    this.progressService.onModuleComplete(this.data.courseId, () =>
      this.loadCourseProgress(false, () => {
        if (this.lockToUnit) return;
        const summaryItem = this.navigationService.findCourseSummaryItem(this.courseStructure);
        if (summaryItem) {
          this.navigateToItem(summaryItem);
          return;
        }
        this.surfaceCompletionBlocked(completingItem);
      })
    );
  }

  /**
   * KAN-367: the learner clicked Complete but the reload did not land on the
   * course summary. Surface an actionable, non-error notification via the
   * app-wide toast instead of doing nothing - but only when something really is
   * unmet. KAN-597: the button is not last-item-only (a mid-course module reads
   * "Complete Module"), and a mid-course module that DID complete must stay
   * silent; the footer offers Next. Decision table:
   * `pure-functions/completion-feedback.ts`.
   */
  private isItemComplete(item: CourseNavigationItem | null): boolean {
    const progress = item ? this.courseProgressMap.get(this.coursePlayerService.getProgressKey(item)) : null;
    return !!(progress?.moduleCompleted || progress?.completed);
  }

  /** KAN-597: a completed item is never pending - its footer already offers Next. */
  private setCompletionPending(pending: boolean): void {
    this.completionPending = pending && !this.isItemComplete(this.currentItem);
    this.cdr.detectChanges();
  }

  private surfaceCompletionBlocked(item: CourseNavigationItem | null): void {
    const items = this.courseStructure?.navigationItems ?? [];
    const feedback = decideCompletionFeedback({
      itemComplete: this.isItemComplete(item),
      isLastItem: !!item && items.indexOf(item) >= items.length - 1,
      allModulesComplete: this.progressService.areAllModulesComplete(this.courseStructure, this.courseProgressMap),
      passingGateSatisfied: this.isPassingGateSatisfied()
    });
    switch (feedback.kind) {
      case 'course-not-passed':
        // KAN-411: every module IS done and the pass verdict blocks - name it.
        this.errorHandler.reportError('Course not passed yet', this.courseVerdictBlockReason ||
          "You have not met this course's passing requirements yet. See the course summary for details.", 'warning');
        return;
      case 'course-incomplete':
        this.errorHandler.reportError('Course not complete yet',
          'Some modules in this course are not finished yet. Modules without a green checkmark in the module list still need to be completed.', 'warning');
        return;
      case 'module-incomplete':
        this.errorHandler.reportError('Module not complete yet',
          this.progressService.describeIncompleteRequirement(item, feedback.finishingCourse), 'warning');
        return;
    }
  }

  /** Gate for the course-summary template subtree (suppressed when lockToUnit). */
  shouldRenderCourseSummary(): boolean {
    return !this.lockToUnit && this.currentItem?.type === 'course_summary';
  }

  /** Decision lives in `pure-functions/override-chip.ts`. */
  shouldShowOverrideChip(): boolean {
    return shouldShowOverrideChipFn({
      playerMode: this.playerMode,
      currentItem: this.currentItem,
      playerConfig: this.playerConfig
    });
  }

  onProgressUpdate(event: any): void {
    this.progressService.onProgressUpdate(
      event,
      this.courseProgressMap,
      this.courseStructure,
      this.cdr,
      () => this.updateNavigationState(),
      this.lockToUnit
    );
  }

  handleQuizSubmit(event: { answers: any[], score: number, passed: boolean, videoProgress?: { furthestPosition: number, duration: number }, attemptId?: string }): void {
    if (!this.currentItem) {
      console.error('[LearnerModal] Cannot submit quiz - no current item');
      return;
    }

    // Reset error state before new submission attempt
    this.quizSubmissionError = false;
    this.quizSubmissionErrorInfo = null;
    // KAN-597: pending from the submit until the progress reload settles, so the
    // scored quiz never flashes "Complete" / "Complete Module" before Next. (The
    // learner path returns without calling back when the item has no quizId.)
    const submittedItem = this.currentItem;
    if (submittedItem.quizId || this.playerMode !== 'learner') this.setCompletionPending(true);

    this.quizSubmitService.handleQuizSubmit(
      event,
      this.currentItem,
      this.data.courseId,
      this.playerMode,
      this.courseProgressMap,
      this.tempInstructorProgress,
      this.courseStructure,
      this.cdr,
      this.timelineComponent,
      () => this.loadCourseProgress(false, () => {
        if (this.currentItem === submittedItem) this.setCompletionPending(false);
      }),
      (info) => {
        this.completionPending = false;
        // Signal quiz-panel to reset spinner on submission error; KAN-349:
        // thread the parsed code/details so attempt-lifecycle 409s get
        // dedicated recovery UI in the panel.
        this.quizSubmissionError = true;
        this.quizSubmissionErrorInfo = info ?? null;
        this.cdr.detectChanges();
      }
    );
  }

  getQuizIdForCurrentModule(): string | null {
    if (!this.currentItem) return null;
    return this.currentItem.quizId || null;
  }

  isQuizCompleted(): boolean {
    if (!this.currentItem) return false;
    return this.currentItem.hasSubmission || false;
  }

  getModuleProgress(moduleId: string): any {
    return getModuleProgress(moduleId, this.courseStructure, this.courseProgressMap, this.coursePlayerService);
  }

  getTotalModulesCount(): number { return countItemsOfType(this.courseStructure, 'module'); }
  getTotalUnitsCount(): number { return countItemsOfType(this.courseStructure, 'unit_intro'); }
  getUnitStats(): { totalModules: number; hasUnitQuiz: boolean } | null {
    return getUnitStatsForItem(this.currentItem, this.courseStructure);
  }
}
